import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CompanyLogo } from '@staffedupusa/shared-components';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { FC, Fragment, useState } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import PreviewFileModal from '@components/PreviewFileModal/PreviewFileModal';
import { Message, Thread } from '@features/messaging';

import { lightPurple, white, primaryBlue, borderGrey } from '@styles/colors';
import { normal } from '@styles/fontWeights';
import { getAvatarFallbackText, getDividerText } from '@features/messaging/components/MessagesList/utils';

type RockerboxMessagesListProps = {
  messages: Message[];
  fullHeight?: boolean;
  onEndReached: () => void;
  isLoading: boolean;
  loggedInUserId: number | undefined;
  headerData: Thread | null;
};

const RockerboxMessagesList: FC<RockerboxMessagesListProps> = ({
  messages,
  fullHeight,
  onEndReached,
  isLoading,
  headerData,
  loggedInUserId,
}) => {
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const groupedMessages = groupMessages(messages);
  if (!messages.length || !groupedMessages.length) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      {isLoading && (
        <Box textAlign="center">
          <CircularProgress color="success" />
        </Box>
      )}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 3,
            borderBottom: '1px solid rgba(128, 129, 140, .25)',
            position: 'sticky',
            left: '0',
            top: '0',
            width: '100%',
            backgroundColor: white,
            zIndex: '2',
          }}
        >
          {headerData && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemAvatar sx={{ minWidth: { xs: 0, sm: 'unset' } }}>
                <Avatar
                  src={headerData.company.s_logo_file || ''}
                  sx={{
                    width: '50px',
                    height: '50px',
                    marginRight: '8px',
                    borderRadius: '6px',
                  }}
                />
              </ListItemAvatar>
              <Typography
                variant="body1"
                sx={{ fontWeight: '600', fontSize: '20px' }}
              >
                {headerData.company?.s_name}
              </Typography>
            </Stack>
          )}
        </Box>
      )}
      <GroupedVirtuoso
        style={{
          height: fullHeight ? '100%' : 300,
          position: 'relative',
        }}
        initialTopMostItemIndex={messages.length - 1}
        groupCounts={groupedMessages.map((group) => group.length)}
        groupContent={(index) => (
          <Divider
            component="li"
            sx={{
              fontSize: '10px',
              color: 'rgba(0, 0, 0, .75)',
              borderColor: '#fff',
              maxWidth: '86%',
              margin: '10px auto',
              '&::before': {
                borderColor: 'rgba(128, 129, 140, 0)',
                zIndex: '-1',
              },
              '&::after': {
                borderColor: 'rgba(128, 129, 140, 0)',
                zIndex: '-1',
              },
            }}
          >
            <span
              style={{
                backgroundColor: borderGrey,
                padding: '2px 5px',
                display: 'inline-block',
                borderRadius: '4px',
                color: 'text.secondary',
                boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
              }}
            >
              {getDividerText(groupedMessages[index][0].dt_timestamp)}
            </span>
          </Divider>
        )}
        startReached={onEndReached}
        followOutput="smooth"
        // components={{
        //   TopItemList: Fragment,
        // }}
        itemContent={(index) => {
          const message = messages[index];
          if (!message) {
            return null;
          }
          const { image, bgcolor, color } = getAvatarImageAndColor(message);

          return (
            <ListItem
              sx={{
                py: 2,
                alignItems: 'end',
                flexDirection:
                  message.sender.id === loggedInUserId ? 'row-reverse' : '',
              }}
              secondaryAction={
                message.message_doc && (
                  <IconButton
                    onClick={() =>
                      setSelectedFilePath(
                        message.message_doc?.file_path || null,
                      )
                    }
                  >
                    <InsertDriveFileIcon />
                  </IconButton>
                )
              }
            >
              <ListItemAvatar
                sx={{
                  position: 'relative',
                  top: '-9px',
                  minWidth: 'unset',
                }}
              >
                {message.sender.company ? (
                  <Box
                    sx={{
                      width: '38px',
                      height: '38px',
                      marginLeft:
                        message.sender.id === loggedInUserId ? '10px' : '0px',
                      marginRight:
                        message.sender.id === loggedInUserId ? '0px' : '10px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '5px',
                      backgroundColor: 'rgba(128, 129, 140, .15)',
                    }}
                  >
                    <CompanyLogo logoUrl={image} maxWidth="30px" />
                  </Box>
                ) : (
                  <Avatar
                    src={image}
                    sx={{
                      bgcolor,
                      color,
                      width: '38px',
                      height: '38px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      marginLeft:
                        message.sender.id === loggedInUserId ? '10px' : '0px',
                      marginRight:
                        message.sender.id === loggedInUserId ? '0px' : '10px',
                    }}
                  >
                    {getAvatarFallbackText(message?.sender?.name || '')}
                  </Avatar>
                )}
              </ListItemAvatar>
              <ListItemText
                sx={{
                  position: 'relative',
                  mt: 0,
                  '& p': {
                    mt: 0,
                  },
                  textAlign:
                    message.sender.id === loggedInUserId ? 'right' : '',
                }}
                primary={
                  <Typography
                    sx={{
                      background:
                        message.sender.id === loggedInUserId
                          ? primaryBlue
                          : 'rgba(128, 129, 140, .15)',
                      maxWidth: 'calc(80%)',
                      fontSize: '13px',
                      borderRadius:
                        message.sender.id === loggedInUserId
                          ? '6px 6px 0 6px'
                          : '6px 6px 6px 0',
                      padding: '9px 12px',
                      color:
                        message.sender.id === loggedInUserId ? white : '#000',
                      wordWrap: 'break-word',
                      width: 'fit-content',
                      marginLeft:
                        message.sender.id === loggedInUserId ? 'auto' : '',
                      textAlign:
                        message.sender.id === loggedInUserId ? 'right' : 'left',
                      '& p:last-child': {
                        mb: 0,
                      },
                    }}
                  >
                    {parse(message.message)}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        display: 'block',
                        marginTop: '2px',
                      }}
                      component="span"
                      fontWeight={normal}
                    >
                      {dayjs(message?.dt_timestamp).format('HH:mm a')}
                    </Typography>
                  </>
                }
                disableTypography
              />
              <PreviewFileModal
                filePath={selectedFilePath}
                handleClose={() => setSelectedFilePath(null)}
              />
            </ListItem>
          );
        }}
      />
    </Stack>
  );
};

const getAvatarImageAndColor = (
  message: Message,
): { image: string; bgcolor: string; color: string } => {
  const image = message?.sender?.photo || message?.sender?.company?.s_logo_file;

  if (image) {
    return {
      image,
      color: 'inherit',
      bgcolor: 'transparent',
    };
  }

  return {
    image: '',
    color: white,
    bgcolor: message?.sender?.company ? lightPurple : 'success.main',
  };
};

function groupMessages(messages: Message[]): Message[][] {
  const groupedMessages: Message[][] = [];
  let currentDate: string | null = null;
  let currentGroup: Message[] = [];

  messages.forEach((message) => {
    const messageDate = dayjs(message.dt_timestamp).format('YYYY-MM-DD');

    if (messageDate !== currentDate) {
      currentGroup = [];
      groupedMessages.push(currentGroup);
      currentDate = messageDate;
    }

    currentGroup.push(message);
  });

  return groupedMessages;
}

export default RockerboxMessagesList;
